import { Component, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError, MatInput } from '@angular/material/input';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import moment from 'moment';
import { IdLoginRequest, CompanyProgressiveRegistrationRequest } from '../../shared/interfaces/id-login.model';
import { API_PARAMS } from '../../shared/constants/api.constants';
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
} from '../../shared/constants/date.constants';
import { MatDialog } from '@angular/material/dialog';
import { DevService } from '../../shared/services/dev.service';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { Router } from '@angular/router';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { OtpDialogComponent } from '../../shared/components/dialogs/otp-dialog/otp-dialog.component';
import { ID_LOGIN_TYPE } from '../../shared/constants/common.constants';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { IdLoginService } from '../../shared/services/id-login.service';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { NgClass } from '@angular/common';
import { RegisterVerifyContactService } from '../../shared/services/register-verify-contact.service';
import { DateInputDirective } from '../../directives/date-input.directive';
import { AlphaNumericOnlyDirective } from '../../directives/alpha-numeric-only.directive';
import { ChineseEnglishSpaceOnlyDirective } from '../../directives/chinese-english-space-only.directive';
import { STORAGE_KEY } from '../../shared/constants/store-key.constants';
import { LoginRegisterService } from '../../shared/services/login-register.service';
import {
  birthdayValidator,
  companyNoValidator,
  empIdValidator,
  idNoValidator,
  nameValidator,
} from '../../shared/utils/validator.util';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import { MaskIdNumberDirective } from '../../directives/mask-id-number.directive';
import { BusinessPlanRequest } from '../../shared/interfaces/reserve-loading.model';
import { MatCheckbox } from "@angular/material/checkbox";
import { AuthService } from '../../shared/services/auth.service';

/**
 * 身分證登入組件
 */
@Component({
  selector: 'app-id-login',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    MatButton,
    MatInput,
    MatError,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatButtonToggleGroup,
    MatButtonToggle,
    FormsModule,
    MatRadioGroup,
    MatRadioButton,
    NgClass,
    DateInputDirective,
    AlphaNumericOnlyDirective,
    ChineseEnglishSpaceOnlyDirective,
    MaskIdNumberDirective,
    MatIconButton,
],
  templateUrl: './id-login.component.html',
  styleUrl: './id-login.component.scss',
})
export class IdLoginComponent {
  isFormSubmit: boolean = false; // 表單是否已經提交
  translateService: TranslateService = inject(TranslateService);
  registerVerifyContactService: RegisterVerifyContactService = inject(
    RegisterVerifyContactService
  );
  authService: AuthService = inject(AuthService);
  idLoginService: IdLoginService = inject(IdLoginService);
  devService: DevService = inject(DevService);
  loginRegisterService: LoginRegisterService = inject(LoginRegisterService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  dialog: MatDialog = inject(MatDialog);
  router: Router = inject(Router);

  nameMaxLength: number = 50;
  idNoMaxLength: number = 10;
  enterpriseCodeMaxLength: number = 10;
  employeeNumberMaxLength: number = 20;

  controlItem: number = 0;

  appointmentProcessing: boolean = false;
  isIdNumberVisible: boolean = false; // 是否顯示身分證號碼
  // 是否為企業預約
  isEnterprise: boolean =
    this.sharedStorageService.get(STORAGE_KEY.RESERVE_TYPE) ===
    ID_LOGIN_TYPE.ENTERPRISE;

  // fillIdNumberChecked: boolean = false;

  /**
   * 1: has the old HIS record in this data
   * 2: find nothing in this data
   * 3: already a member in new system, go to login
   * 4: this data already has appoinment
   * 5: this data has already done the health check with 150 days
   * 6: has HIS, but have no contact of both phone and email, call customer service
   * 7: corporate reservation expired (this flowStep only for corporate)
   * 8: corporate has uploaded data, but no reservation code (this flowStep only for corporate)
   * 9: corporate has uploaded data, but the reservation code is not for this employee (this flowStep only for corporate)
   */
  registerCondition?: string = '';
  phoneNumber: string = '';
  email: string = '';

  ngOnInit() {
    // 載入並回填企業身份資訊
    this.loadSavedEnterpriseData();

    // 取得控制項
    this.controlItem = this.sharedStorageService.get('controlItem') ?? 0;
    // 若控制項為 0，則顯示身分證字號欄位
    if (this.controlItem === 0) {
      this.idLoginForm.get('idNo')?.setValidators([
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
        Validators.pattern(/^[A-Za-z0-9]+$/),
      ]);

      // 移除員工編號的驗證器
      if (this.isEnterprise) {
        this.idLoginForm.get('employeeNumber')?.clearValidators();
        this.idLoginForm.get('employeeNumber')?.updateValueAndValidity();
      }
    }

    this.idLoginForm.valueChanges.subscribe(() => {
      if (this.isFormSubmit) {
        // 觸發所有欄位重新驗證
        Object.keys(this.idLoginForm.controls).forEach(key => {
          this.idLoginForm.get(key)?.updateValueAndValidity({ emitEvent: false });
        });
      }
    });

    // this.idLoginForm.get('idNo')?.valueChanges.subscribe(value => {
    //   if (this.fillIdNumberChecked && this.isEnterprise) {
    //     this.idLoginForm.get('employeeNumber')?.setValue(value || '', { emitEvent: false });
    //   }
    // });

    // // 監聽身分證字號欄位，確保只保留英數字符
    // this.idLoginForm.get('idNo')?.valueChanges.subscribe(value => {
    //   if (value) {
    //     const cleanValue = value.replace(/[^A-Za-z0-9]/g, '');
    //     if (cleanValue !== value) {
    //       this.idLoginForm.get('idNo')?.setValue(cleanValue, { emitEvent: false });
    //     }
    //   }
    // });
  }

  // 預約表單
  idLoginForm = new FormGroup({
    birthday: new FormControl<moment.Moment | null>(null, [
      Validators.required,
      (control: FormControl) =>
        birthdayValidator(control, this.translateService, {
          max: 100,
          min: 18,
        }),
    ]),
    name: new FormControl<string>('', [
      Validators.required,
      noSpecialCharsValidator,
      (control: FormControl) =>
        nameValidator(control, this.translateService, this.nameMaxLength),
    ]),
    idNo: new FormControl<string>('', [
      Validators.required,
      (control: FormControl) => idNoValidator(control, this.translateService),
    ]),
    ...(this.isEnterprise && {
      // enterpriseCode: new FormControl<string>('', [
      //   Validators.required,
      //   (control: FormControl) =>
      //     companyNoValidator(control, this.translateService),
      // ]),
      // 將生日欄位清除驗證
      birthday: new FormControl<moment.Moment | null>(null),
      employeeNumber: new FormControl<string>('', [
        (control: AbstractControl): ValidationErrors | null => {
          // 如果是空值，不進行驗證 (選填)
          if (!control.value || control.value.trim() === '') {
            return null;
          }
          return empIdValidator(control as FormControl, this.translateService);
        },
      ]),
      identity: new FormControl<string>(API_PARAMS.IDENTITY.EMPLOYEE, [
        Validators.required,
        noSpecialCharsValidator,
      ]),
    }),
  });

  // 獲取驗證生日錯誤訊息
  getBirthdayErrorMessage() {
    const control = this.idLoginForm.get('birthday');
    if (!control || !control.errors || !this.isFormSubmit) return '';

    const errors = control.errors;

    // mat-datepickerParse錯誤
    if (errors['matDatepickerParse']) {
      return this.translateService.instant('error.birthday-error.format');
    }
    if (errors['required']) {
      return this.translateService.instant('error.birthday-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    if (errors['invalidAge']) {
      return errors['invalidAge'];
    }
    return '';
  }

  // 獲取驗證姓名錯誤訊息
  getNameErrorMessage() {
  const control = this.idLoginForm.get('name');
  if (!control || !control.errors || !this.isFormSubmit) return '';

  const errors = control.errors;

  // 統一判斷 noSpecialCharsValidator 的錯誤 key
  const noSpecialKeys = ['onlyWhitespace', 'whitespace', 'specialChar'];

  if (errors['required'] || noSpecialKeys.some(key => errors[key])) {
    return this.translateService.instant('error.required', {
      fieldName: this.translateService.instant('id-login.text'),
    });
  }

  // 其他錯誤
  if (errors['invalidCharacters']) {
    return errors['invalidCharacters'];
  }
  if (errors['maxLengthExceeded']) {
    return errors['maxLengthExceeded'];
  }

  return '';
}


  // 獲取驗證身份證號錯誤訊息
  getIdNoErrorMessage() {
    const control = this.idLoginForm.get('idNo');
    if (!control || !control.errors || !this.isFormSubmit) return '';

    const errors = control.errors;
    const noSpecialKeys = ['onlyWhitespace', 'whitespace', 'specialChar'];

    if (errors['required'] || noSpecialKeys.some(key => errors[key])) {
      return this.translateService.instant('error.id-number-error.required');
    }
    if (errors['invalidCharacters']) {
      return errors['invalidCharacters'];
    }
    
    return '';
  }

  // 獲取驗證企業代碼錯誤訊息
  getEnterpriseCodeErrorMessage() {
    const control = this.idLoginForm.get('enterpriseCode');
    if (!control || !control.errors || !this.isFormSubmit) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant(
        'error.enterprise-code-error.required'
      );
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
  }

  // 獲取驗證員工編號錯誤訊息
  getEmployeeNumberErrorMessage() {
    const control = this.idLoginForm.get('employeeNumber');
    if (!control || !control.errors || !this.isFormSubmit) return '';

    const errors = control.errors;
    // if (errors['required']) {
    //   return this.translateService.instant(
    //     'error.employee-number-error.required'
    //   );
    // }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
  }

  /**
   * 提交預約表單
   * 根據預約類型（個人/企業）呼叫不同的 API 進行漸進式驗證
   */
  onSubmit() {
    console.log('進入 id-login，預約類型:', this.isEnterprise ? '企業' : '個人');
    
    // 防止重複提交
    if (this.appointmentProcessing) {
      return;
    }

    this.isFormSubmit = true;

    // 表單驗證失敗，不執行後續邏輯
    if (!this.idLoginForm.valid) {
      return;
    }

    this.appointmentProcessing = true;

    // ========== 取得共用欄位資料 ==========
    const idNo = this.idLoginForm.get('idNo')?.value ?? '';
    const name = this.idLoginForm.get('name')?.value ?? '';

    // 儲存登入請求資料供後續使用
    const idLoginRequest: IdLoginRequest = {
      name: name,
      idNo: idNo,
    };

    if (this.isEnterprise) {
      // idLoginRequest.enterpriseCode = this.idLoginForm.get('enterpriseCode')!.value!;
      idLoginRequest.identity = this.idLoginForm.get('identity')!.value!;
      
      const employeeNumber = this.idLoginForm.get('employeeNumber')?.value?.trim();
      if (employeeNumber) {
        idLoginRequest.employeeNumber = employeeNumber;
      }
    }

    this.sharedStorageService.set(STORAGE_KEY.ID_LOGIN_REQUEST, idLoginRequest);

    // 依據預約類型執行不同流程
    if (!this.isEnterprise) {
      // 個人漸進式驗證流程
      this.handlePersonalProgressiveFlow(idNo);
    } else {
      // 企業漸進式驗證流程
      this.handleEnterpriseProgressiveFlow(idNo);
    }
  }

  /**
   * 從 SharedStorage 載入並回填企業身份資訊
   */
  private loadSavedEnterpriseData() {
    if (!this.isEnterprise) return;

    const savedData = this.sharedStorageService.get(STORAGE_KEY.ID_LOGIN_REQUEST);
    
    if (savedData) {
      // 回填共用欄位
      if (savedData.name) {
        this.idLoginForm.get('name')?.setValue(savedData.name);
      }
      if (savedData.idNo) {
        this.idLoginForm.get('idNo')?.setValue(savedData.idNo);
      }
      // if (savedData.birthday) {
      //   // birthday 需要轉換為 moment 物件
      //   this.idLoginForm.get('birthday')?.setValue(moment(savedData.birthday, API_DATE_FORMAT));
      // }

      // 回填企業特有欄位
      // if (savedData.enterpriseCode) {
      //   this.idLoginForm.get('enterpriseCode')?.setValue(savedData.enterpriseCode);
      // }
      if (savedData.employeeNumber) {
        this.idLoginForm.get('employeeNumber')?.setValue(savedData.employeeNumber);
      }
      if (savedData.identity) {
        this.idLoginForm.get('identity')?.setValue(savedData.identity);
      }

      // 回填完成，觸發驗證
      this.idLoginForm.updateValueAndValidity();

      console.log('已回填企業身份資訊:', savedData);
    }
  }

  /**
   * 處理個人漸進式驗證流程
   */
  private handlePersonalProgressiveFlow(idNo: string) {
    this.idLoginService
      .progressiveverification({idNo})
      .subscribe({
        next: (res) => {
          this.registerCondition = res.statusCode === 'S0000' ? res.data?.flowStep : '2';
          console.log('個人漸進式驗證結果 flowStep:', this.registerCondition);

          // 根據 flowStep 處理不同情境
          switch (this.registerCondition) {
            case '6':
              // has HIS, but have no contact of both phone and email, call customer service
              this.openCallServiceDialog();
              break;

            case '5':
              // this data has already done the health check with 150 days
              this.openAlreadyCompleteRecentlyDialog();
              break;

            case '4':
              // this data already has appoinment, go to register
              this.openGoToRegisterDialog();
              break;

            case '3':
              // already a member in new system, go to login
              this.openGoToLoginDialog('has-member');
              break;

            case '2':
              // find nothing in this data (身份驗證完至選擇地點頁面)
              this.handleNoHisDataFlow(ID_LOGIN_TYPE.PERSONAL);
              break;

            case '1':
              // has the old HIS record in this data, need otp verification
              this.handleHisDataWithOtpFlow(res.data);
              break;

            default:
              console.warn('未預期的 flowStep:', this.registerCondition);
              this.handleNoHisDataFlow(ID_LOGIN_TYPE.PERSONAL);
              break;
          }

          this.finalizeSubmission();
        },
        error: (err) => {
          console.error('個人漸進式驗證 API 錯誤:', err);
          this.handleApiError(err);
        }
      });
  }

  /**
   * 處理企業漸進式驗證流程
   */
  private handleEnterpriseProgressiveFlow(idNo: string) {
    console.log("進入企業漸進式驗證流程");
    const employeeNumber = this.idLoginForm.get('employeeNumber')?.value?.trim();
    
    const companyRequest: CompanyProgressiveRegistrationRequest = {
      companyNo: this.sharedStorageService.get('companyNo') ?? '',
      employeeId: employeeNumber ?? '',
      idNo: idNo,
      familyFlag: this.idLoginForm.get('identity')!.value!,
    };

    if (employeeNumber) {
      companyRequest.employeeId = employeeNumber;
    }

    console.log('企業漸進式驗證請求:', companyRequest);

    this.idLoginService
      .companyProgressiveRegistration(companyRequest)
      .subscribe({
        next: (res) => {
          // 處理 API 回傳非成功的狀態碼

          // E2001:控制項為識別碼，提醒必填
          if (res.statusCode === 'E2001') {
            this.openSponsorAmtZeroDialog();
            return;
          }
          else if (res.statusCode !== 'S0000') {
            this.handleEnterpriseApiError(res);
            this.finalizeSubmission();
            return;
          }

          this.phoneNumber = res.data?.cell || '';
          this.email = res.data?.email || '';

          this.registerCondition = res.data?.flowStep || '2';
          console.log('企業漸進式驗證結果 flowStep:', this.registerCondition);

          // 根據 flowStep 處理不同情境
          switch (this.registerCondition) {
            case '10':
              // sponsor amt is zero，no HIS
              this.openSponsorAmtZeroDialog();
              break;
            case '9':
              // sponsor amt is zero，has filled
              this.openSponsorAmtZeroDialog();
              break;

            case '8':
              // sponsor amt is zero，has HIS
              this.openSponsorWithHisAmtZeroDialog();
              break;

            case '7':
              // status: employee number already used
              this.openEmployeeNumberAlreadyUsedDialog();
              break;

            case '6':
              // has HIS, but have no contact of both phone and email, call customer service
              this.openCallServiceDialog();
              break;

            case '5':
              // this data has already done the health check with 150 days
              this.openEnterpriseAlreadyCompletedDialog();
              break;

            case '4':
              // this data already has appoinment
              this.openGoToRegisterDialog();
              break;

            case '3':
              // already a member in new system, go to login
              this.openGoToLoginDialog('has-member');
              break;

            case '2':
              // find nothing in this data (身份驗證完至選擇地點頁面)
              this.handleNoHisDataFlow(ID_LOGIN_TYPE.ENTERPRISE);
              break;

            case '1':
              // has the old HIS record in this data
              // 企業 API 回應結構與個人不同，cell 和 email 直接在 data 層
              this.handleHisDataWithOtpFlow(res.data, true);
              break;

            default:
              console.warn('未預期的 flowStep:', this.registerCondition);
              this.handleNoHisDataFlow(ID_LOGIN_TYPE.ENTERPRISE);
              break;
          }

          this.finalizeSubmission();
        },
        error: (err) => {
          console.error('企業漸進式驗證 API 錯誤:', err);
          this.handleApiError(err);
        }
      });
  }
  
  /**
   * 處理 HIS 查無資料的情境（flowStep = 2）
   * 設定為無 HIS 狀態並導航至選擇地點頁面
   */
  private handleNoHisDataFlow(reserveType: string) {
    const currentReserveType = this.sharedStorageService.get('reserveType');
    if (!currentReserveType) {
      this.sharedStorageService.set('reserveType', reserveType);
    }
    this.sharedStorageService.set(STORAGE_KEY.LOGIN_STATUS, 'withoutHIS');

    // 如果是企業預約，需要額外設定 userPlanRequest 和 businessPlanRequest
    if (this.isEnterprise) {
      this.setEnterpriseRequestData();
    }

    this.router.navigate(['/healthcheck/reserve/location']).then();
  }

  /**
   * 設定企業預約所需的請求資料
   */
  private setEnterpriseRequestData() {
    const userPlanRequest: UserPlanRequest = {
      orgNo: '',
      gender: '',
      birthday: '',
      age: '',
      idNo: this.idLoginForm.get('idNo')!.value!,
      name: this.idLoginForm.get('name')!.value!,
      cell: '',
      email: '',
      telH: '',
      telO: '',
      zipCode: '',
      history: [],
      family: [],
      subjectiveSymptoms: [],
      identityType: this.idLoginForm.get('identity')!.value!,
      funcType: '1',
    };
    this.sharedStorageService.set(STORAGE_KEY.USER_PLAN_REQUEST, userPlanRequest);

    const businessPlanRequest: BusinessPlanRequest = {
      orgNo: '',
      employeeId: this.idLoginForm.get('employeeNumber')?.value?.trim() || '',
      companyNo: this.sharedStorageService.get('companyNo') ?? '',
      gender: '',
    };
    this.sharedStorageService.set(STORAGE_KEY.BUSINESS_PLAN_REQUEST, businessPlanRequest);
  }

  /**
   * 處理 HIS 有舊資料需 OTP 驗證的情境（flowStep = 1）
   * @param data API 回應的 data 物件
   * @param isEnterprise 是否為企業流程（企業的資料結構不同）
   */
  private handleHisDataWithOtpFlow(data: any, isEnterprise: boolean = false) {
    console.log('HIS 資料 (OTP 驗證前):', data);

    // 企業與個人的 API 回應結構不同
    if (isEnterprise) {
      // 企業：直接從 data 取得 cell 和 email
      this.phoneNumber = data?.cell || '';
      this.email = data?.email || '';
      this.setEnterpriseRequestData();
    } else {
      // 個人：從 data.contact 取得 cell 和 email
      this.phoneNumber = data?.contact?.cell || '';
      this.email = data?.contact?.email || '';
    }

    console.log('解析後聯絡資訊 - 手機:', this.phoneNumber, '信箱:', this.email);

    // 驗證是否有有效的聯絡方式
    const validPhone = this.phoneNumber && this.phoneNumber !== '0' && /^\d{10}$/.test(this.phoneNumber);
    const validEmail = this.email && this.email.trim() !== '';

    if (!validPhone && !validEmail) {
      // 兩者都無效，請聯繫客服
      this.openCallServiceDialog();
    } else {
      // 有有效聯絡方式，開啟 OTP 驗證
      const otpChannel = validPhone ? 'cell' : 'email';
      const otpContact = validPhone ? this.phoneNumber : this.email;
      console.log('開啟 OTP 驗證 - 管道:', otpChannel, '聯絡方式:', otpContact);
      this.openOtpDialog(otpChannel, otpContact);
    }
  }

  /**
   * 完成提交流程的清理工作
   */
  private finalizeSubmission() {
    this.isFormSubmit = false;
    this.appointmentProcessing = false;
  }

  /**
   * 處理 API 錯誤
   */
  private handleApiError(error: any) {
    console.error('API 錯誤詳情:', error);
    
    // 根據錯誤狀態碼顯示不同訊息
    let errorTitle = this.translateService.instant('error.general.title');
    let errorMessage = this.translateService.instant('error.general.message');

    if (error.error?.statusCode) {
      const statusCode = error.error.statusCode;
      
      // 根據 API 文件的錯誤碼處理
      switch (statusCode) {
        case 'E4001':
          errorMessage = this.translateService.instant('error.enterprise-code-employee-error');
          break;
        case 'EC6002':
          errorMessage = this.translateService.instant('error.verification-failed');
          break;
        default:
          errorMessage = error.error.errorMessage || errorMessage;
          break;
      }
    }

    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: errorTitle,
        message: errorMessage,
        showCloseButton: true,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => this.closeDialog(),
      },
    });

    this.finalizeSubmission();
  }

  // 開啟前往註冊的Dialog (無 Web 會員但已有預約)
  openGoToRegisterDialog() {
    //2025.10.8更新:不管有無Web會員都統一引導去登入，無Web會員在登入時再被引到去註冊
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant(
          'id-login.login-already-reserved-dialog.title' // "您已經有一筆檢查預約"
        ),
        message: this.translateService.instant(
          'id-login.login-already-reserved-dialog.message'
        ),
        showCloseButton: true,
        closeAction: () => {
          this.router.navigate(['/']).then(r => this.closeDialog());
        },
        buttonText: this.translateService.instant(
          'id-login.button.login-to-review' // "前往會員中心查看"
        ),
        action: () => {
          // 統一導航到登入頁面
          this.router.navigate(['/healthcheck/login'], {
            // queryParams: { tab: 'register' }
          }).then(r => this.closeDialog());
        },
      },
    });
  } 

  // 開啟前往登入的Dialog 已有預約/已有會員
  openGoToLoginDialog(reason: string) {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant(
          reason === 'has-reserve'
            ? 'id-login.already-reserved-dialog.title'
            : 'login-register.already-have-account'
        ),
        message:
          reason === 'has-reserve'
            ? this.translateService.instant(
                'id-login.already-reserved-dialog.message'
              )
            : '',
        showCloseButton: true,
        buttonText: this.translateService.instant(
          'id-login.button.login-to-review'
        ),
        action: () => {
          // 如果是企業 flowStep=3，導向登入頁面，並標記登入後要去企業登入頁面
          if (reason === 'has-member' && this.isEnterprise && this.registerCondition === '3') {
            // 清除可能干擾的 storage
            this.sharedStorageService.remove(STORAGE_KEY.LOGIN_STATUS);
            this.sharedStorageService.remove('reserveStep');
            this.sharedStorageService.remove(STORAGE_KEY.USER_PLAN_REQUEST);
            
            const enterpriseCode = this.idLoginForm.get('enterpriseCode')?.value || '';
            const idNo = this.idLoginForm.get('idNo')?.value ?? '';
            
            // 取得員工編號或使用身分證 (12/16 若沒填預約代碼，不帶身分證)
            const employeeNumber = this.idLoginForm.get('employeeNumber')?.value?.trim();


            const familyFlag = this.idLoginForm.get('identity')?.value || API_PARAMS.IDENTITY.EMPLOYEE;
          
            // 只有當 employeeNumber 存在且不為空時，才傳入
            const redirectData: any = {
              shouldRedirect: true,
              enterpriseCode: enterpriseCode,
              familyFlag: familyFlag,
            };
            
            // 只在有員工編號時才加入 empId
            if (employeeNumber) {
              redirectData.empId = employeeNumber;
            }
            
            // 儲存到 storage，讓登入頁面知道要導向企業登入
            this.sharedStorageService.set(STORAGE_KEY.REDIRECT_TO_COMPANY_LOGIN, redirectData);
            
            this.router.navigate(['/healthcheck/login']).then(r => this.closeDialog());
          } else {
            this.router.navigate(['/healthcheck/login']).then(r => this.closeDialog());
          }
        },
      },
    });
  }

  // flowStep = 5，代表該身分已在該企業使用過 - 企業
  openEnterpriseAlreadyCompletedDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant(
          'id-login.employee-already-completed.title'
        ),
        message: this.translateService.instant(
          'id-login.employee-already-completed.message'
        ),
        showCloseButton: false,
        buttonText: this.translateService.instant('common.button.i-understood'),
        action: () => this.router.navigate(['/healthcheck']).then(r => this.closeDialog()),
      },
    });
  }

  // 開啟近期已完成健檢服務Dialog - 個人
  openAlreadyCompleteRecentlyDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant(
          'id-login.already-complete-recently.title'
        ),
        message: this.translateService.instant(
          'id-login.already-complete-recently.message'
        ),
        showCloseButton: true,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => this.router.navigate(['/']).then(r => this.closeDialog()),
      },
    });
  }

  // 開啟客服電話Dialog
  openCallServiceDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant(
          'id-login.call-customer-service.title'
        ),
        message: this.translateService.instant(
          'id-login.call-customer-service.message'
        ),
        showCloseButton: true,
        buttonText: this.translateService.instant(
          'common.button.confirm'
        ),
        action: () =>
          this.router
            .navigate(['/healthcheck'])
            .then(r => this.closeDialog()),
      },
    });
  }

  // ========== flowStep 7: 合約期間內員工編號已被使用 Dialog ==========
  openEmployeeNumberAlreadyUsedDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: false,
      data: {
        title: this.translateService.instant(
          'id-login.employee-number-already-used.title'
        ),
        message: this.translateService.instant(
          'id-login.employee-number-already-used.message'
        ),
        showCloseButton: false,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => this.router.navigate(['/']).then(r => this.closeDialog()),
      },
    });
  }
  // ========== flowStep 7: 企業預約已到期 Dialog ==========
  // openEnterpriseExpiredDialog() {
  //   this.dialog.open(SingleActionDialogComponent, {
  //     disableClose: true,
  //     data: {
  //       title: this.translateService.instant(
  //         'id-login.enterprise-expired.title'
  //       ),
  //       message: this.translateService.instant(
  //         'id-login.enterprise-expired.message'
  //       ),
  //       showCloseButton: true,
  //       buttonText: this.translateService.instant('common.button.confirm'),
  //       action: () => this.router.navigate(['/']).then(r => this.closeDialog()),
  //     },
  //   });
  // }

  // ========== flowStep 8: 可預約，有HIS，要驗otp，但補助金額為 0 Dialog ==========
  openSponsorWithHisAmtZeroDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: false,
      data: {
        title: this.translateService.instant(
          'id-login.sponsor-amt-zero.title'
        ),
        message: this.translateService.instant(
          'id-login.sponsor-amt-zero.message'
        ),
        showCloseButton: false,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => {
          this.setEnterpriseRequestData();
          this.closeDialog();

          setTimeout(() => {
            // 判斷要發送到手機還是信箱
            const hasValidPhone = !!(this.phoneNumber && this.phoneNumber !== '0' && /^\d{10}$/.test(this.phoneNumber));
            const otpType = hasValidPhone ? API_PARAMS.PHONE : API_PARAMS.EMAIL;
            const contact = hasValidPhone ? this.phoneNumber : this.email;
            
            this.openOtpDialog(otpType, contact);
          }, 300);
        },
      },
    });
  }

  // ========== flowStep 9: 可預約，沒HIS，但補助金額為 0 Dialog ==========
  openSponsorAmtZeroDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: false,
      data: {
        title: this.translateService.instant(
          'id-login.sponsor-amt-zero.title'
        ),
        message: this.translateService.instant(
          'id-login.sponsor-amt-zero.message'
        ),
        showCloseButton: false,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => {
          this.setEnterpriseRequestData();
          this.router.navigate(['/healthcheck/reserve/location']).then(r => this.closeDialog());
        },
      },
    });
  }


  // ========== flowStep 8: 企業有上傳資料但未傳預約代碼 Dialog ==========
  // openEnterpriseNoReservationCodeDialog() {
  //   this.dialog.open(SingleActionDialogComponent, {
  //     disableClose: true,
  //     data: {
  //       title: this.translateService.instant(
  //         'id-login.enterprise-no-reservation-code.title'
  //       ),
  //       message: this.translateService.instant(
  //         'id-login.enterprise-no-reservation-code.message'
  //       ),
  //       showCloseButton: true,
  //       buttonText: this.translateService.instant('common.button.i-understood'),
  //       action: () => this.router.navigate(['/healthcheck/idlogin']).then(r => this.closeDialog()),
  //     },
  //   });
  // }

  // ========== flowStep 9: 企業預約代碼與員工不符 Dialog ==========
  openEnterpriseReservationCodeMismatchDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: false,
      data: {
        title: this.translateService.instant(
          'id-login.enterprise-reservation-code-mismatch.title'
        ),
        message: this.translateService.instant(
          'id-login.enterprise-reservation-code-mismatch.message'
        ),
        showCloseButton: true,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => this.router.navigate(['/healthcheck/idlogin']).then(r => this.closeDialog()),
      },
    });
  }

  // 開啟錯誤顯示Dialog
  openOtpErrorDialog() {
    const title = this.translateService.instant(
      'error.otp-error.reached-otp-limit'
    );
    const message = '';

    const errorDialog = this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: title,
        message: message,
        showCloseButton: true,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => errorDialog.close(),
      },
    });
  }

  // 開啟otp驗證dialog
  openOtpDialog(otpType: string, contact: string) {
    const idNo = this.idLoginForm.get('idNo')?.value ?? '';

    // 驗證有效手機(0或非數字會被認為是false)
    const hasValidPhone = !!(this.phoneNumber && this.phoneNumber !== '0' && /^\d{10}$/.test(this.phoneNumber));

    this.registerVerifyContactService
      .registerSendOtp(idNo, otpType, contact)
      .subscribe(res => {
        if (res.statusCode === 'S0000') {
          const otpDialog = this.dialog.open(OtpDialogComponent, {
            disableClose: true,
            data: {
              isOtpPhone: hasValidPhone,
              canSwitch: this.phoneNumber && this.email,
              phoneNumber: this.phoneNumber,
              email: this.email,
              idNo: idNo,
              isFromlogin: false,
              successAction: () => {
                this.sharedStorageService.set(
                  STORAGE_KEY.LOGIN_STATUS,
                  'withHIS'
                );
                // 根據預約類型設定 reserveType
                const reserveType = this.isEnterprise 
                  ? ID_LOGIN_TYPE.ENTERPRISE 
                  : ID_LOGIN_TYPE.PERSONAL;
                this.sharedStorageService.set('reserveType', reserveType);
                this.router
                  .navigate(['/healthcheck/reserve/location'])
                  .then(() => otpDialog.close());
              },
            },
          });
        } else if (res.statusCode === 'EC5004') {
          // otp attempt reach limit
          this.openOtpErrorDialog();
        }
      });
  }

  /**
   * 處理企業漸進式驗證的 API 錯誤回應
   * @param res API 回應物件
   */
  private handleEnterpriseApiError(res: any) {
    const statusCode = res.statusCode?.trim() || ''; // 加上 trim() 去除前後空格
    let title = '';
    let message = '';
    let showCloseButton = false;
    let buttonText = this.translateService.instant('common.button.confirm');
    let navigateTo = '';

    // 根據不同的錯誤代碼設定對應的 Dialog 內容
    switch (statusCode) {
      case 'EL1007':
        // 查無可用公司基本資料 -> 查無此企業代碼
        title = this.translateService.instant('id-login.enterprise-not-found.title');
        message = this.translateService.instant('id-login.enterprise-not-found.message');
        showCloseButton = false;
        break;

      case 'EL1013':
        // 公司基本資料有誤，請聯絡系統管理員 -> 查無此企業代碼
        title = this.translateService.instant('id-login.enterprise-not-found.title');
        message = this.translateService.instant('id-login.enterprise-not-found.message');
        showCloseButton = false;
        break;

      default:
        // 預設的錯誤處理 - 只有在 default 時才使用 API 的 errorMessage
        title = this.translateService.instant('error.general.title');
        message = res.errorMessage || this.translateService.instant('error.general.message');
        showCloseButton = true;
        break;
    }

    // 顯示錯誤 Dialog
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: title,
        message: message,
        showCloseButton: showCloseButton,
        buttonText: buttonText,
        action: () => {
          this.closeDialog();
          // 如果需要導航到特定頁面
          if (navigateTo) {
            this.router.navigate([navigateTo]);
          }
        },
      },
    });
  }

  // 關閉Dialog
  closeDialog() {
    this.dialog.closeAll();
  }

  // onFillIdNumberChange(checked: boolean) {
  //   this.fillIdNumberChecked = checked;
    
  //   if (checked) {
  //     // 取得身分證號碼的值
  //     const idNo = this.idLoginForm.get('idNo')?.value ?? '';
      
  //     // 將身分證號碼帶入員工編號欄位
  //     if (idNo) {
  //       this.idLoginForm.get('employeeNumber')?.setValue(idNo);
  //     }
  //   } else {
  //     // 取消勾選時清空員工編號
  //     this.idLoginForm.get('employeeNumber')?.setValue('');
  //   }
  // }

  // 切換身分證字號可見性
  toggleIdNumberVisibility() {
    this.isIdNumberVisible = !this.isIdNumberVisible;
  }

  // dialog中點擊登入查看
  // loginToReview() {
  //   this.closeDialog();
  //   this.router.navigate(['/healthcheck/login']).then(r => {});
  // }

  protected readonly DATE_FORMAT = DATE_FORMAT;
  protected readonly API_PARAMS = API_PARAMS;
}
/** 檢查是否包含特殊符號或頭尾空白 */
export function noSpecialCharsValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  // 如果是 null 或空字串，不在這裡報錯，交給 Validators.required 處理
  if (value == null) return null;

  const trimmed = value.trim();

  // 若整個字串都是空白（例如 "   "）
  if (trimmed.length === 0) {
    return { onlyWhitespace: true };
  }

  // 若有頭尾空白
  if (trimmed !== value) {
    return { whitespace: true };
  }

  // 若含有特殊符號（允許中英文、數字、空白中間）
  const regex = /^[a-zA-Z0-9\u4e00-\u9fa5\s]+$/;
  if (!regex.test(value)) {
    return { specialChar: true };
  }

  return null;
}